import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { GetRootState } from 'js/src/libs/RootState';
import type Luminary from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { getNonRecommendableAssetNames } from '../../video-state/serialization/assetSerializer';
import { findWidowLineIndices, getRenderedTextLines } from '../../video-state/renderedTextLines';
import type { LintFinding } from '../videoLinter';

/**
 * Warning: a multi-line text asset renders with a line holding a single
 * stranded word (a widow/orphan). Uses the renderer's measured line breakdown,
 * so assets without metrics (not yet rendered) are skipped rather than
 * guessed at. One-line assets are never flagged — a lone short label is fine.
 */
export function checkTextWidows(luminary: Luminary, getState: GetRootState): LintFinding[] {
    const findings: LintFinding[] = [];

    luminary.scenes.forEach((scene) => {
        const nonRecommendable = getNonRecommendableAssetNames(scene, getState());
        const textAssets = scene
            .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
            .filter(
                (asset) =>
                    asset.isEditable && asset.text?.trim() && !nonRecommendable.has(asset.name),
            );

        textAssets.forEach((asset) => {
            const lines = getRenderedTextLines(asset);
            if (!lines || lines.length < 2) {
                return;
            }
            const widowIndices = findWidowLineIndices(lines, asset.text);
            if (widowIndices.length === 0) {
                return;
            }
            const rendered = lines.map((line) => `"${line.text}"`).join(' / ');
            findings.push({
                check: 'text_widows',
                severity: 'warning',
                sceneId: scene.id,
                assetName: asset.name,
                message: `Text asset "${asset.name}" wraps with a single stranded word on line ${widowIndices
                    .map((index) => index + 1)
                    .join(
                        ' and ',
                    )} (rendered as ${rendered}). Adjust the box width or font_size, or shorten the text, so every line holds at least two words. Load the ${SKILL_NAMES.fixTextWrapping} skill for the fix procedure.`,
            });
        });
    });

    return findings;
}
